import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Divider,
  LinearProgress,
  Chip,
  useTheme,
  alpha,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Alert,
  CardHeader,
  IconButton,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  TrendingUp,
  People,
  QrCodeScanner,
  Assignment,
  Person,
  DateRange,
  Star,
  Visibility,
  Add as AddIcon,
} from "@mui/icons-material";
import { useQuery } from "@apollo/client";
import {
  GET_MY_AUTH,
  GET_APPLICANTS,
  GET_INSIGHTS,
  GET_STANDS,
} from "../graphql/queries";
import { useNavigate } from "react-router-dom";
import { SimplePieChart, SimpleAreaChart } from "../Components/SimpleCharts";
import {
  format,
  formatDate,
  timestampToDate,
  getRelativeTimeString,
  parseDateString,
} from "../utils/dateUtils";
import { styled } from "@mui/material/styles";

// Activity Feed Component
const ActivityFeed = ({ activities, loading }) => {
  return (
    <Card elevation={3} sx={{ height: "100%" }}>
      <CardHeader
        title="Recent Activity"
        action={
          <IconButton size="small">
            <RefreshIcon />
          </IconButton>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <List sx={{ maxHeight: 400, overflow: "auto" }}>
            {activities.map((activity, index) => (
              <ListItem
                key={index}
                alignItems="flex-start"
                divider={index < activities.length - 1}
                sx={{ px: 0 }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor: getActivityColor(activity.type),
                    }}
                  >
                    {getActivityIcon(activity.type)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1">
                      {activity.title}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.primary"
                      >
                        {activity.description}
                      </Typography>
                      <Typography
                        component="div"
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {getRelativeTimeString(
                          timestampToDate(activity.timestamp)
                        )}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

// Helper functions for activity feed
const getActivityColor = (type) => {
  switch (type) {
    case "scan":
      return "primary.main";
    case "hot":
      return "error.main";
    case "warm":
      return "warning.main";
    case "cold":
      return "info.main";
    case "staff":
      return "secondary.main";
    default:
      return "info.main";
  }
};

const getActivityIcon = (type) => {
  switch (type) {
    case "scan":
      return <QrCodeScanner fontSize="small" />;
    case "hot":
    case "warm":
    case "cold":
      return <Person fontSize="small" />;
    case "staff":
      return <People fontSize="small" />;
    default:
      return <Assignment fontSize="small" />;
  }
};

// Stat Card Component
const StatCard = ({
  title,
  value,
  icon,
  color,
  increase,
  subtitle,
  loading,
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        height: "100%",
        position: "relative",
        overflow: "hidden",
        transition: "transform 0.3s ease",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -20,
          right: -20,
          width: 120,
          height: 120,
          borderRadius: "50%",
          backgroundColor: alpha(color, 0.1),
          zIndex: 0,
        }}
      />
      <CardContent sx={{ position: "relative", zIndex: 1 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography
              variant="h6"
              color="text.secondary"
              fontWeight="regular"
            >
              {title}
            </Typography>
            {loading ? (
              <CircularProgress size={24} sx={{ my: 2 }} />
            ) : (
              <Typography
                variant="h4"
                sx={{ mt: 1, mb: 1, fontWeight: "bold" }}
              >
                {value}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {increase && !loading && (
              <Typography
                variant="body2"
                sx={{
                  color: increase.startsWith("-")
                    ? "error.main"
                    : "success.main",
                  display: "flex",
                  alignItems: "center",
                  mt: 1,
                }}
              >
                <TrendingUp fontSize="small" sx={{ mr: 0.5 }} />
                {increase} today
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              backgroundColor: alpha(color, 0.2),
              color: color,
              width: 48,
              height: 48,
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};

// Format date function to convert timestamps to readable format
// const formatDate = (timestamp) => {
//   if (!timestamp) return "N/A";

//   try {
//     const date = new Date(timestamp);
//     return date.toLocaleString();
//   } catch (error) {
//     return timestamp;
//   }
// };

// Function to get time difference from now
const getTimeDifference = (dateValue) => {
  if (!dateValue) return "N/A";

  try {
    // Use our existing utilities for date handling
    // This will handle the 2025 year issue and all timestamp conversions
    return getRelativeTimeString(
      typeof dateValue === "number" || /^\d+$/.test(dateValue)
        ? timestampToDate(dateValue)
        : typeof dateValue === "string" && dateValue.includes("2025-")
        ? parseDateString(dateValue)
        : new Date(dateValue)
    );
  } catch (error) {
    console.error("Error calculating time difference:", error, dateValue);
    return "Recently"; // Fallback for any errors
  }
};

const DashboardPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalLeads: 0,
    hotLeads: 0,
    warmLeads: 0,
    coldLeads: 0,
    scannedToday: 0,
    conversionRate: 0,
    staffCount: 0,
  });
  const [leadsByTime, setLeadsByTime] = useState([]);
  const [leadsByType, setLeadsByType] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [staffData, setStaffData] = useState([]);

  // Debug function for timestamps (can be removed in production)
  useEffect(() => {
    const { debugDateConversion } = require("../utils/dateUtils");
    if (debugDateConversion) {
      // Test with the specific timestamp that's causing the issue (if known from the backend)
      const testTimestamp = 1727520708000;
      console.log("===== TIMESTAMP DEBUG =====");
      debugDateConversion(testTimestamp, "2025-04-28 22:43:03");
      console.log("==========================");
    }
  }, []);

  // Get auth data
  const { data: authData, loading: authLoading } = useQuery(GET_MY_AUTH);
  const eventId = authData?.eventId;
  const standId = authData?.standId;
  const companyName = authData?.company || "Your Company";
  const eventName = authData?.event?.name || "";
  const allowedStaffCount = authData?.allowed || "2";
  const isAdmin = authData?.me === "admin";

  // Staff data query
  const { data: standsData, loading: standsLoading } = useQuery(GET_STANDS, {
    variables: { eventId },
    skip: !eventId,
    onCompleted: (data) => {
      if (data && data.stands) {
        setStaffData(data.stands);
        setStats((prev) => ({
          ...prev,
          staffCount: data.stands.length,
        }));
      }
    },
  });

  // Get all leads with detailed data
  const {
    data: applicantsData,
    loading: applicantsLoading,
    refetch: refetchApplicants,
  } = useQuery(GET_APPLICANTS, {
    variables: {
      conditions: {
        limit: 1000,
      },
      role: "all",
    },
    skip: !eventId || !standId,
    onCompleted: (data) => {
      processApplicantsData(data);
    },
    onError: (error) => {
      console.error("Error fetching applicants:", error);
      setError("Failed to load lead data");
    },
  });

  // Get insights data
  const {
    data: insightData,
    loading: insightsLoading,
    refetch: refetchInsights,
  } = useQuery(GET_INSIGHTS, {
    variables: { eventId, standId },
    skip: !eventId || !standId,
    onCompleted: (data) => {
      processInsightsData(data);
    },
    onError: (error) => {
      console.error("Error fetching insights:", error);
    },
  });

  // Function to process applicants data
  const processApplicantsData = (data) => {
    if (!data || !data.applicants) return;

    const applicants = data.applicants || [];

    // Debug the first lead's timestamp (if available)
    if (applicants.length > 0 && applicants[0].created) {
      const { debugDateConversion } = require("../utils/dateUtils");
      if (debugDateConversion) {
        console.log("===== LEAD TIMESTAMP DEBUG =====");
        debugDateConversion(applicants[0].created);
        console.log("==============================");
      }
    }

    // Helper function to parse "MMM d" dates
    const parseMonthDay = (dateStr) => {
      try {
        if (!dateStr || typeof dateStr !== "string") {
          console.error("Invalid date string:", dateStr);
          return new Date(0); // Return epoch date for invalid input
        }

        const parts = dateStr.split(" ");
        if (parts.length < 2) {
          console.error("Invalid date format:", dateStr);
          return new Date(0);
        }

        const month = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ].indexOf(parts[0]);
        if (month === -1) {
          console.error("Invalid month:", parts[0]);
          return new Date(0);
        }

        const day = parseInt(parts[1]);
        if (isNaN(day)) {
          console.error("Invalid day:", parts[1]);
          return new Date(0);
        }

        return new Date(new Date().getFullYear(), month, day);
      } catch (error) {
        console.error("Error parsing date:", dateStr, error);
        return new Date(0);
      }
    };

    // Count lead types
    let hotLeads = 0;
    let warmLeads = 0;
    let coldLeads = 0;
    let todaysLeads = 0;

    // Get today's date as string for comparison
    const today = new Date().toDateString();

    // Recent activity from leads data
    const recentActivities = [];

    // Temporary store for daily leads tracking
    const dailyLeads = {};

    // Process each lead
    applicants.forEach((applicant) => {
      // Count by lead type
      if (applicant.type === "Hot Lead") hotLeads++;
      else if (applicant.type === "Warm Lead") warmLeads++;
      else if (applicant.type === "Cold Lead") coldLeads++;
      else coldLeads++; // Default to cold if type not recognized

      // Check if created today
      // Convert Unix timestamp to Date object
      const createdDate = timestampToDate(applicant.created);
      if (!createdDate) {
        console.error("Invalid timestamp:", applicant.created);
        return;
      }

      const createdDateString = createdDate.toDateString();
      if (createdDateString === today) {
        todaysLeads++;
      }

      // Format for daily chart data - use date only
      const dateKey = format(createdDate, "MMM d");

      if (!dailyLeads[dateKey]) dailyLeads[dateKey] = 0;
      dailyLeads[dateKey]++;

      // Add to recent activity
      try {
        let visitorName = "Unknown Visitor";
        if (applicant.data) {
          const data =
            typeof applicant.data === "string"
              ? JSON.parse(applicant.data)
              : applicant.data;

          // Try to find a name field in the data
          visitorName =
            data["Full Name"] ||
            data["Your Name"] ||
            data["Name"] ||
            (data["First Name"] && data["Last Name"]
              ? `${data["First Name"]} ${data["Last Name"]}`
              : data["First Name"] || data["Last Name"]) ||
            "Unknown Visitor";
        }
        const activity = {
          type: applicant.type?.toLowerCase() || "scan",
          title: `${applicant.type || "New Lead"} Added`,
          description: `${visitorName} ${
            applicant.remark ? `- ${applicant.remark}` : ""
          }`,
          timestamp: applicant.created, // Store the raw timestamp
        };
        recentActivities.push(activity);
      } catch (error) {
        console.error("Error processing lead data for activity:", error);
      }
    });

    // Sort recent activities by time (most recent first)
    recentActivities.sort((a, b) => {
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

    // Take only the 5 most recent activities
    setRecentActivity(recentActivities.slice(0, 5));

    // Create chart data for leads over time
    const timeData = Object.keys(dailyLeads)
      .sort((a, b) => parseMonthDay(a) - parseMonthDay(b))
      .slice(-7) // Only the last 7 days
      .map((date) => ({
        name: date,
        leads: dailyLeads[date],
      }));

    // Debug logs to help diagnose the issue
    console.log("dailyLeads:", dailyLeads);
    console.log("timeData:", timeData);

    setLeadsByTime(timeData);

    // Create lead by type data
    setLeadsByType([
      { name: "Hot Leads", value: hotLeads, color: theme.palette.error.main },
      {
        name: "Warm Leads",
        value: warmLeads,
        color: theme.palette.warning.main,
      },
      { name: "Cold Leads", value: coldLeads, color: theme.palette.info.main },
    ]);

    // Update stats
    setStats((prev) => ({
      ...prev,
      totalLeads: applicants.length,
      hotLeads,
      warmLeads,
      coldLeads,
      scannedToday: todaysLeads,
      conversionRate:
        applicants.length > 0
          ? Math.round((hotLeads / applicants.length) * 100)
          : 0,
    }));
  };

  // Process insights data if available
  const processInsightsData = (data) => {
    if (!data?.getInsights) return;

    try {
      const insights = JSON.parse(data.getInsights);

      if (insights.allScans) {
        // Use this data to supplement or adjust our stats if needed
        const todayScans = insights.allScans.filter((scan) => {
          if (!scan.created) return false;
          const scanDate = timestampToDate(scan.created);
          if (!scanDate) return false;

          const today = new Date();
          return scanDate.toDateString() === today.toDateString();
        });

        setStats((prev) => ({
          ...prev,
          scannedToday: todayScans.length,
        }));
      }

      if (insights.staffData) {
        setStaffData(insights.staffData);
        setStats((prev) => ({
          ...prev,
          staffCount: insights.staffData.length,
        }));
      }
    } catch (error) {
      console.error("Error parsing insights data:", error);
    }
  };

  // Update loading state when data fetching completes
  useEffect(() => {
    setLoading(authLoading || applicantsLoading || insightsLoading);
  }, [authLoading, applicantsLoading, insightsLoading]);

  const handleRefresh = () => {
    setLoading(true);
    Promise.all([refetchApplicants(), refetchInsights()])
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error refreshing data:", error);
        setError("Failed to refresh data");
        setLoading(false);
      });
  };

  // Custom cards with routes - filtered based on admin status
  const allQuickAccessCards = [
    {
      title: "Scan Badges",
      description: "Scan visitor badges and collect leads",
      icon: <QrCodeScanner fontSize="large" />,
      path: "/scanning-visitors",
      color: theme.palette.primary.main,
      adminOnly: false,
    },
    {
      title: "View Leads",
      description: "View and manage all your collected leads",
      icon: <People fontSize="large" />,
      path: "/my-visitors",
      color: theme.palette.secondary.main,
      adminOnly: false,
    },
    {
      title: "Insights",
      description: "Analytics and performance metrics",
      icon: <TrendingUp fontSize="large" />,
      path: "/insights",
      color: theme.palette.success.main,
      adminOnly: true,
    },
    {
      title: "Manage Staff",
      description: "Add and manage your team members",
      icon: <Person fontSize="large" />,
      path: "/staff",
      color: theme.palette.warning.main,
      adminOnly: true,
    },
  ];

  // Filter quick access cards based on admin status
  const quickAccessCards = allQuickAccessCards.filter(
    (card) => !card.adminOnly || isAdmin
  );

  // If there's an error, display it
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: "1.5rem", md: "2rem" },
              fontWeight: "bold",
              mb: 1,
            }}
          >
            Welcome to {eventName}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Here's what's happening with your event today
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          variant="outlined"
          disabled={loading}
        >
          Refresh Data
        </Button>
      </Box>

      {/* Loading indicator */}
      {loading && <LinearProgress sx={{ mb: 3 }} />}

      {/* Key stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Leads"
            value={stats.totalLeads}
            icon={<People />}
            color={theme.palette.primary.main}
            subtitle="All time total"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Hot Leads"
            value={stats.hotLeads}
            icon={<Star />}
            color={theme.palette.error.main}
            subtitle={
              stats.totalLeads > 0
                ? `${Math.round(
                    (stats.hotLeads / stats.totalLeads) * 100
                  )}% of total`
                : "0% of total"
            }
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Scanned Today"
            value={stats.scannedToday}
            icon={<QrCodeScanner />}
            color={theme.palette.info.main}
            increase={`+${stats.scannedToday}`}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Conversion Rate"
            value={`${stats.conversionRate}%`}
            icon={<TrendingUp />}
            color={theme.palette.success.main}
            subtitle="Hot leads to total"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Quick Access Section */}
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Quick Actions
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {quickAccessCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: "100%",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: theme.shadows[10],
                  backgroundColor: alpha(card.color, 0.05),
                },
              }}
              onClick={() => navigate(card.path)}
            >
              <CardContent sx={{ textAlign: "center", p: 3 }}>
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    mx: "auto",
                    backgroundColor: alpha(card.color, 0.1),
                    color: card.color,
                    mb: 2,
                  }}
                >
                  {card.icon}
                </Avatar>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {card.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts and Recent Activities */}
      <Grid container spacing={3}>
        {/* Lead Distribution by Type */}
        <Grid item xs={12} md={5} lg={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Lead Distribution
              </Typography>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                  <CircularProgress />
                </Box>
              ) : stats.totalLeads === 0 ? (
                <Box sx={{ textAlign: "center", py: 5 }}>
                  <Typography color="text.secondary">
                    No lead data available yet
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    height: 250,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SimplePieChart data={leadsByType} height={250} width={300} />
                </Box>
              )}
              {!loading && stats.totalLeads > 0 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  {leadsByType.map((entry, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", alignItems: "center", mx: 1 }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          bgcolor: entry.color,
                          mr: 1,
                        }}
                      />
                      <Typography variant="body2">
                        {entry.name}: {entry.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Leads Over Time */}
        <Grid item xs={12} md={7} lg={8}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Leads Over Time
              </Typography>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                  <CircularProgress />
                </Box>
              ) : leadsByTime.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 5 }}>
                  <Typography color="text.secondary">
                    No time-based lead data available yet
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ height: 250 }}>
                  <SimpleAreaChart
                    data={leadsByTime}
                    height={250}
                    width={600}
                    dataKey="leads"
                    xAxisDataKey="name"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Staff Status */}
        {isAdmin && (
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">Staff Status</Typography>
                  <Chip
                    label={`${stats.staffCount}/${allowedStaffCount} Used`}
                    color={
                      stats.staffCount >= Number(allowedStaffCount)
                        ? "error"
                        : "success"
                    }
                    size="small"
                  />
                </Box>
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : staffData.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Typography color="text.secondary">
                      No staff members found
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ px: 2 }}>
                    {staffData.slice(0, 3).map((staff, index) => (
                      <React.Fragment key={staff.id || index}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            my: 2,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Avatar
                              sx={{
                                mr: 2,
                                bgcolor: theme.palette.primary.main,
                              }}
                            >
                              {staff.name?.charAt(0) || "S"}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2">
                                {staff.name || "Unknown"}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {staff.email || "No email"}
                              </Typography>
                            </Box>
                          </Box>
                          {/* <Chip
                            label={
                              index === 0
                                ? "Online"
                                : "Last active: " +
                                  getTimeDifference(staff.created)
                            }
                            color={index === 0 ? "success" : "default"}
                            size="small"
                          /> */}
                        </Box>
                        {index < staffData.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </Box>
                )}
                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    disabled={stats.staffCount >= Number(allowedStaffCount)}
                    onClick={() => navigate("/staff")}
                    fullWidth
                  >
                    {stats.staffCount >= Number(allowedStaffCount)
                      ? "Staff Limit Reached"
                      : "Add Staff Member"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Recent Activity */}
        <Grid item xs={12} md={8}>
          <ActivityFeed activities={recentActivity} loading={loading} />
        </Grid>

        {/* Event Information */}
        <Grid item xs={12}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.1
              )}, ${alpha(theme.palette.secondary.main, 0.1)})`,
              borderRadius: 2,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" sx={{ mb: 1, fontWeight: "bold" }}>
                    {authData?.event?.name || `Event #${eventId || "Unknown"}`}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Maximize your lead generation with powerful analytics and
                    easy scanning
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Chip
                      icon={<DateRange />}
                      label={`${stats.totalLeads} total leads collected`}
                      variant="outlined"
                      color="primary"
                    />
                    <Chip
                      icon={<Star />}
                      label={`${stats.hotLeads} hot leads collected`}
                      variant="outlined"
                      color="error"
                    />
                    <Chip
                      icon={<People />}
                      label={`${stats.staffCount}/${allowedStaffCount} staff members`}
                      variant="outlined"
                      color={
                        stats.staffCount >= Number(allowedStaffCount)
                          ? "error"
                          : "success"
                      }
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4} sx={{ textAlign: "center" }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate("/scanning-visitors")}
                    startIcon={<QrCodeScanner />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      boxShadow: theme.shadows[5],
                      textTransform: "none",
                      fontSize: "1.1rem",
                    }}
                  >
                    Start Scanning
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
